import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import ListLayout from '../stories/children-components/ListLayout';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

const ModulePreview = ({ module, theme, structures, mainColor }) => {
  const { t } = useTranslation();
  const listType = module?.list?.tabs?.[0]?.listType;
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    // Reset selected item and structure when module changes
    setSelectedItem(null);
    setSelectedStructure(null);
    setSelectedTab(0);
  }, [module]);

  const handleItemClick = (item) => {
    setSelectedItem(item);
    if (module.structureId) {
      const structure = structures.find((s) => s.id === module.structureId);
      setSelectedStructure(structure);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const renderStructurePreview = () => {
    if (!selectedStructure || !selectedStructure.tabs) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minWidth: 'auto',
                px: 2,
                py: 0,
                fontSize: '0.75rem',
                height: '20px',
                lineHeight: '20px',
                minHeight: '20px',
                maxHeight: '20px',
                textTransform: 'none',
                '&.Mui-selected': {
                  color: mainColor || theme.palette.text.primary,
                  fontWeight: 600,
                },
              },
            }}
          >
            {selectedStructure.tabs.map((tab, index) => (
              <Tab
                sx={{
                  height: '20px',
                  lineHeight: '20px',
                  minHeight: '20px',
                  maxHeight: '20px',
                }}
                key={index}
                label={
                  tab.name_fr || tab.name_en || tab.name || `Tab ${index + 1}`
                }
              />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ marginTop: '-30px' }}>
          <ResponsiveGridLayout
            className="layout"
            layouts={{
              lg:
                selectedStructure?.tabs[selectedTab]?.blocks?.map((block) => ({
                  i: block.i,
                  x: block.x || 0,
                  y: block.y || 0,
                  w: block.w || 4,
                  h: block.h || 4,
                  minW: 1,
                  minH: 1,
                  maxW: 12,
                  maxH: 12,
                  static: true,
                })) || [],
            }}
            breakpoints={{ lg: 1200, md: 996, sm: 568, xs: 280, xxs: 0 }}
            cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
            rowHeight={8}
            margin={[10, 10]}
            containerPadding={[8, 8]}
            isDraggable={false}
            isResizable={false}
          >
            {selectedStructure.tabs[selectedTab]?.blocks?.map((block) => (
              <Paper
                key={block.i}
                elevation={1}
                sx={{
                  p: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {block.name_fr ||
                    block.name_en ||
                    block.name ||
                    `Block ${block.i}`}
                </Typography>
                <div className="mt-2">
                  {' '}
                  <Typography
                    fontSize={9}
                    sx={{ fontWeight: 400, lineHeight: 1 }}
                  >
                    {block?.infos?.text || ''}
                  </Typography>
                </div>
                {block?.contentType === 'content' && (
                  <div className="mt-2">
                    <div>
                      <Skeleton variant="text" width="100%" height={25} />
                      <div
                        style={{
                          height: 10,
                          backgroundColor: '#69696960',
                          width: '100px',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <div className="mt-3">
                      <Skeleton variant="text" width="90%" height={25} />
                      <div
                        style={{
                          height: 10,
                          backgroundColor: '#69696960',
                          width: '100px',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <div className="mt-3">
                      <Skeleton variant="text" width="95%" height={25} />
                      <div
                        style={{
                          height: 10,
                          backgroundColor: '#69696960',
                          width: '100px',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <div className="mt-3">
                      <Skeleton variant="text" width="70%" height={25} />
                      <div
                        style={{
                          height: 10,
                          backgroundColor: '#69696960',
                          width: '100px',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                  </div>
                )}
                {block?.contentType === 'list' && <div className="mt-2"></div>}
                {block?.contentType === 'component' && (
                  <div className="mt-2"></div>
                )}
                {block?.contentType === 'action' && (
                  <div className="mt-2"></div>
                )}
              </Paper>
            ))}
          </ResponsiveGridLayout>
        </Box>
      </Box>
    );
  };

  const getPreviewContent = () => {
    if (selectedItem) {
      return renderStructurePreview();
    }

    switch (listType) {
      case 'listA':
      case 'listS':
        return (
          <List dense>
            {Array.from({ length: 5 }).map((_, index) => (
              <ListItem
                button
                key={index}
                onClick={() => handleItemClick({ id: `item-${index}` })}
                selected={selectedItem?.id === `item-${index}`}
              >
                <ListItemText
                  primaryTypographyProps={{ fontSize: 9, fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: 7 }}
                  primary={`${
                    module.name_fr || module.name_en || module.name
                  } ${index + 1}`}
                  secondary={t('element')}
                />
              </ListItem>
            ))}
          </List>
        );
      case 'kanban':
        return (
          <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
            {['To Do', 'In Progress', 'Done'].map((status, index) => (
              <Paper
                key={status}
                sx={{
                  p: 1,
                  width: '200px',
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {status}
                </Typography>
                <Paper
                  sx={{
                    p: 1,
                    mb: 1,
                    bgcolor: theme.palette.background.default,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleItemClick({ id: `item-${index}` })}
                >
                  {module.name_fr || module.name_en || module.name} {index + 1}
                </Paper>
              </Paper>
            ))}
          </Box>
        );
      case 'calendar':
        return (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 1,
                mt: 2,
              }}
            >
              {Array.from({ length: 7 }).map((_, i) => (
                <Paper
                  key={i}
                  sx={{
                    p: 1,
                    height: '100px',
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleItemClick({ id: `item-${i}` })}
                >
                  <Typography variant="caption">
                    {new Date(2024, 0, i + 1).toLocaleDateString('en-US', {
                      weekday: 'short',
                    })}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        );
      case 'table':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('preview.table')}
            </Typography>
            <Box
              sx={{
                mt: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.background.paper,
                }}
              >
                {['Name', 'Description', 'Status'].map((header) => (
                  <Typography
                    key={header}
                    variant="subtitle2"
                    sx={{
                      p: 1,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {header}
                  </Typography>
                ))}
              </Box>
              {Array.from({ length: 3 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&:last-child': { borderBottom: 'none' },
                    cursor: 'pointer',
                  }}
                  onClick={() => handleItemClick({ id: `item-${i}` })}
                >
                  {['Sample Item', 'Description', 'Active'].map((cell) => (
                    <Typography
                      key={cell}
                      variant="body2"
                      sx={{
                        p: 1,
                        borderRight: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {cell}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        );
      default:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('preview.noPreview')}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        m: 0,
        borderRadius: '8px',
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
      }}
    >
      {getPreviewContent()}
    </Paper>
  );
};

export default ModulePreview;
